import { forwardRef, type ChangeEvent, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { IconClose20, IconExSearch } from '@/components/ui/icons'
import { cn } from '@/lib/utils'

interface ExploreSearchRowProps {
  value: string
  onChange: (value: string) => void
  onClose: () => void
}

/** Figma `button-md` in the icon-only shape — 36px, radius/xs. */
function CloseButton({
  onClose,
  className,
}: {
  onClose: () => void
  className?: string
}): ReactElement {
  const { t } = useTranslation()

  return (
    <button
      type="button"
      onClick={onClose}
      aria-label={t('actions.close')}
      className={cn(
        'text-icon hover:bg-btn-secondary-raised dark:hover:bg-panel-sidebar size-9 shrink-0 cursor-pointer items-center justify-center rounded-xs p-2 transition-colors',
        className,
      )}
    >
      <IconClose20 className="size-5" aria-hidden="true" />
    </button>
  )
}

/**
 * Figma `panel-list` (2267:290713) — the search field with the ⌘K badge and
 * the close button.
 *
 * Below 900px the frames split this in two: the close button takes a row of
 * its own above the field and the badge disappears, because there is no
 * keyboard to press it with (2267:291249 · 291251).
 *
 * Focus is deliberately NOT managed here: the parent must only focus the
 * input programmatically on fine-pointer devices (see `hasFinePointer` in
 * `@/lib/pointer`). On touch devices the user focuses the field by tapping
 * it, which opens the on-screen keyboard on the very first tap.
 *
 * The zoom-on-focus problem is fixed in CSS (see `mobile-input-zoom.css`):
 * iOS Safari zooms into any focused input whose font-size is below 16px.
 */
export const ExploreSearchRow = forwardRef<HTMLInputElement, ExploreSearchRowProps>(
  function ExploreSearchRow({ value, onChange, onClose }, ref): ReactElement {
    const { t } = useTranslation()

    return (
      <>
        <div
          data-node-id="2267:291249"
          data-name="panel-list"
          className="flex w-full items-center justify-end p-2 md:hidden"
        >
          <CloseButton onClose={onClose} className="flex" />
        </div>

        <div
          data-node-id="2267:290713"
          data-name="panel-list"
          className="hub-rule flex w-full items-center gap-2 p-2"
        >
          <label className="bg-input-surface flex min-w-px flex-1 items-center gap-1 rounded-xs p-2">
            <IconExSearch className="text-input-icon size-5 shrink-0" aria-hidden="true" />
            <input
              ref={ref}
              type="search"
              inputMode="search"
              enterKeyHint="search"
              autoComplete="off"
              autoCorrect="off"
              value={value}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                onChange(event.target.value)
              }}
              placeholder={t('explore.searchPlaceholder')}
              aria-label={t('explore.title')}
              className="hub-search-input hub-label-md text-widget-primary placeholder:text-widget-primary h-5 w-full min-w-px bg-transparent outline-none"
            />
          </label>

          {/* <span
            data-name="button-sm"
            className="bg-btn-secondary-raised text-widget-secondary hub-label-lg hidden h-[22px] shrink-0 items-center justify-center gap-0.5 rounded-xs px-1.5 md:flex"
          >
            <IconExCommand className="size-[16px] shrink-0" aria-hidden="true" />
            <span aria-hidden="true">K</span>
            <span className="sr-only">{t('explore.shortcut')}</span>
          </span> */}

          <CloseButton onClose={onClose} className="hidden md:flex" />
        </div>
      </>
    )
  },
)
